"use client";
import { Size } from "@/api/AppApi/common-interfaces/api-enums";
import { memo } from "react";
import { ITheme } from "@/api/AppApi/services/company/interfaces/ICompany";
import { getProductBoxDefaultDimAndSpacing } from "../../common/functions";
import EnableSellingTooltip from "./EnableSellingTooltip";
import useSellingMode from "@/hooks/useSellingMode";
import { ProductBoxContextProvider } from "../context/ProductBoxContext";
import { IProductMinimizedData } from "@/app/components/pages-components/product-details-page/details-belpers/product-attributes/helpers/interfaces";
import {
  ProductPreviewTypes,
  unit,
} from "@/api/AppApi/services/product/interfaces/api-product-details-interface";
import { useLazyVisible } from "@/hooks/useLazyVisible";
import { Link } from "@/app/components/lib/navigation/Link";

interface Props {
  size: Size;
  scrollableTabs: boolean;
  theme: ITheme;
  children: any;
  isGrid: boolean;
  minimizedProduct: IProductMinimizedData;
  units: unit[];
  preview_type: ProductPreviewTypes;
  type: string;
  bundleSetSelectedChildIndex?: (index: number | null) => void;
  priority?: boolean;
  /** Product name — rendered inside the placeholder link so the card is crawlable before it mounts. */
  productName?: string;
}

const ProductCard: React.FC<Props> = ({
  size,
  scrollableTabs,
  children,
  isGrid,
  minimizedProduct,
  units,
  preview_type,
  type,
  bundleSetSelectedChildIndex,
  priority,
  productName,
}) => {
  // Initial lazy mount: skeleton until first scroll into view
  // keepMounted = true (default) → once mounted, stays in DOM forever
  // Above-the-fold cards (priority) mount eagerly so the LCP image is present
  // in the first paint / SSR markup instead of waiting for the observer.
  const { ref, isVisible } = useLazyVisible("1200px", true, !!priority);
  const smallBox = isSmallProductBox(size);
  const classname = smallBox
    ? `new-product-box-small${
        scrollableTabs && isGrid ? "-scrollable-tabs-grid" : ""
      }`
    : `new-product-box-large${
        scrollableTabs && isGrid ? "-scrollable-tabs-grid" : ""
      }`;

  // A STATIC height floor — deliberately NOT `content-visibility: auto`.
  //
  // `content-visibility: auto` size-contains the card, which made `contain-intrinsic-size`
  // the card's ONLY source of height: the grid rows are auto-sized, and the skeleton's
  // `padding-top` hack lives inside the contained subtree, so it contributes nothing while
  // the card is skipped. On iOS/WebKit builds where `content-visibility` misbehaves the
  // cards stayed skipped and un-sized — every row collapsed to the card's padding (~52px)
  // and the card's `overflow: hidden` clipped the content away, so a whole category page
  // rendered as rows of empty placeholder strips (reported on iPhone 12 Pro / 16, in both
  // Safari and Chrome — same WebKit — while other iPhones were fine). Measured in WebKit:
  // intrinsic size honoured → 460px cards, ignored → 52px cards.
  //
  // `min-height` reserves the same space, cannot be skipped by any engine feature, and
  // costs nothing. The off-screen work is already avoided by the lazy mount below.
  const sizeStyle: React.CSSProperties = {
    minHeight: getProductBoxDefaultDimAndSpacing(size).height,
  };

  return (
    <div ref={ref} className={classname} style={sizeStyle}>
      {isVisible ? (
        <ProductBoxContextProvider
          product={minimizedProduct}
          units={units}
          preview_type={preview_type}
          type={type}
          bundleSetSelectedChildIndex={bundleSetSelectedChildIndex}
        >
          <VisibleContent>{children}</VisibleContent>
        </ProductBoxContextProvider>
      ) : (
        <div
          className={`skeleton-rect${scrollableTabs ? "-hor-product-box" : ""}`}
          style={{
            width: "100%",
            paddingTop: `${`max(${
              getProductBoxDefaultDimAndSpacing(size).height
            }px,100%`})`,
          }}
        >
          {/* The card mounts lazily on scroll, but the link must exist in the
              server HTML regardless: without it a category page shipped ZERO
              crawlable /products/ links, so products were discoverable only via
              the sitemap. Visually hidden (not display:none) so it stays
              crawlable and reachable by assistive tech without disturbing the
              placeholder. */}
          {minimizedProduct?.slug && (
            <Link
              skipCheckingForLang
              prefetch={false}
              href={`/products/${minimizedProduct.slug}`}
              tabIndex={-1}
              aria-hidden="true"
              style={{
                position: "absolute",
                width: 1,
                height: 1,
                overflow: "hidden",
                clip: "rect(0 0 0 0)",
                whiteSpace: "nowrap",
              }}
            >
              {productName ?? minimizedProduct.slug}
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

/** Moves useSellingMode inside the visible branch to avoid 432 unnecessary hook subscriptions */
const VisibleContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { enableAddToCart } = useSellingMode();
  return (
    <EnableSellingTooltip enableSelling={enableAddToCart}>
      {children}
    </EnableSellingTooltip>
  );
};

export default memo(ProductCard);

const isSmallProductBox = (size: Size) =>
  [Size.small, Size.vertical_small].includes(size);
